import { isAxiosError } from "axios";
import { apiClient } from "@/shared/services/api";

// ── Shared types ────────────────────────────────────────────────

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface SalesSummaryScope {
  from: string;
  to: string;
}

export interface SalesSummaryFilters {
  businessUnit?: "auto" | "all" | "greenhouse" | "agroforestry";
  regionId?: string;
  cropId?: string;
  varietyId?: string;
  search?: string;
  sortBy?: "sales" | "quantity" | "orders" | "region";
  sortDescending?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

export interface BusinessUnitSalesDashboard {
  role: "BusinessUnit";
  scope: SalesSummaryScope;
  businessUnit: "all" | "greenhouse" | "agroforestry";
  allowedBusinessUnits: Array<"all" | "greenhouse" | "agroforestry">;
  totalSales: number;
  totalOrders: number;
  regions: Array<{ name: string; sales: number; id: string | null }>;
  items: Array<{ name: string; sales: number }>;
  itemTotalCount: number;
  itemPageNumber: number;
  itemPageSize: number;
  crops: SalesSummaryLookupOption[];
  varieties: SalesSummaryLookupOption[];
  unclassifiedLineCount: number;
  unclassifiedAmount: number;
  accounting?: FinancialDashboardAccounting;
}

export interface SalesSummaryLookupOption { id: string; name: string }

export interface RegionalProductSalesRow {
  cropId: string;
  cropName: string;
  varietyId: string | null;
  varietyName: string;
  unit: "Trays" | "Pieces";
  trayType: string | null;
  quantity: number;
  invoicedSales: number;
}

export interface RegionalSalesRow {
  regionId: string | null;
  regionName: string;
  invoicedSales: number;
  trayQuantity: number;
  pieceQuantity: number;
  orderCount: number;
  customerCount: number;
  products: RegionalProductSalesRow[];
}

export interface RegionalSalesReport {
  items: RegionalSalesRow[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalInvoicedSales: number;
  totalTrayQuantity: number;
  totalPieceQuantity: number;
  regions: SalesSummaryLookupOption[];
  crops: SalesSummaryLookupOption[];
  varieties: SalesSummaryLookupOption[];
}

export interface KpiDelta {
  current: number;
  previous: number;
  change: number;
}

export interface FinancialDashboardAccounting {
  recognizedRevenue: number | null;
  postedCashReceived: number | null;
  postedAccountsReceivable: number | null;
  postedInvoiceOutstanding: number;
  accountsReceivableVariance: number | null;
  operationalUnpostedInvoiceOutstanding: number;
  uninvoicedOrderBalances: number;
  pendingPaymentApprovalAmount: number;
  pendingPaymentApprovalCount: number;
  legacyOrUnclassifiedRevenue: number;
  revenueVariance: number;
  asOfExclusiveUtc: string;
  definitionVersion: string;
}

export interface TopCustomerRow {
  customerId: string;
  customerName: string;
  orderCount: number;
  totalSpend: number;
}

export interface OrderRow {
  id: string;
  orderNumber: string;
  orderDate: string;
  customerName: string;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
  paymentStatus: string;
  fulfillmentStatus: string;
  status: string;
}

// ── Sales Rep ───────────────────────────────────────────────────

export interface SalesRepKpis {
  ordersCreated: KpiDelta;
  totalRevenue: KpiDelta;
  grossSales: number;
  invoicedRevenue: number;
  cashCollected: number;
  outstandingReceivables: number;
  averageOrderValue: number;
  fullyFulfilled: number;
  partiallyFulfilled: number;
  awaitingFulfillment: number;
  accounting?: FinancialDashboardAccounting;
}

export interface SalesRepDashboard {
  role: "SalesRep";
  scope: SalesSummaryScope;
  kpis: SalesRepKpis;
  topCustomers: TopCustomerRow[];
  myOrders: OrderRow[];
}

// ── Sales Manager ───────────────────────────────────────────────

export interface SalesManagerKpis {
  totalRevenue: KpiDelta;
  grossSales: number;
  invoicedRevenue: number;
  cashCollected: number;
  outstandingReceivables: number;
  ordersCount: KpiDelta;
  walkInOrdersCount: number;
  walkInRevenue: number;
  scheduledOrdersCount: number;
  scheduledRevenue: number;
  outstandingBalance: number;
  pendingFulfillments: number;
  accounting?: FinancialDashboardAccounting;
}

export interface SalesRepLeaderboardRow {
  userId: string;
  userName: string;
  orderCount: number;
  revenue: number;
}

export interface TopVarietyRow {
  cropType: string;
  variety: string | null;
  revenue: number;
  trayCount: number;
}

export interface TrayTypeBreakdownRow {
  trayType: string;
  revenue: number;
  trayCount: number;
}

export interface SalesManagerDashboard {
  role: "SalesManager";
  scope: SalesSummaryScope;
  kpis: SalesManagerKpis;
  salesRepLeaderboard: SalesRepLeaderboardRow[];
  topCustomers: TopCustomerRow[];
  topVarieties: TopVarietyRow[];
  trayTypeBreakdown: TrayTypeBreakdownRow[];
  regionalSales: RegionalSalesReport;
}

// ── GM ──────────────────────────────────────────────────────────

export interface AgingBuckets {
  zeroToThirty: number;
  thirtyOneToSixty: number;
  sixtyPlus: number;
}

export interface FulfillmentByStage {
  sowing: number;
  germinating: number;
  growing: number;
  readyForDispatch: number;
}

export interface GmKpis {
  totalRevenue: KpiDelta;
  grossSales: number;
  invoicedRevenue: number;
  cashCollected: number;
  outstandingReceivables: number;
  ordersCount: KpiDelta;
  walkInRevenueShare: number;
  outstandingBalance: number;
  aging: AgingBuckets;
  pendingFulfillmentsByStage: FulfillmentByStage;
  accounting?: FinancialDashboardAccounting;
}

export interface MonthlyRevenuePoint {
  month: string;
  revenue: number;
}

export interface SeedlingTypeBreakdownRow {
  type: string;
  revenue: number;
}

export interface GmDashboard {
  role: "GM";
  scope: SalesSummaryScope;
  kpis: GmKpis;
  revenueByMonth: MonthlyRevenuePoint[];
  trayTypeBreakdown: TrayTypeBreakdownRow[];
  seedlingTypeBreakdown: SeedlingTypeBreakdownRow[];
  topCustomersBar: TopCustomerRow[];
  regionalSales: RegionalSalesReport;
}

// ── POS ─────────────────────────────────────────────────────────

export interface PosKpis {
  walkInOrdersToday: number;
  walkInRevenueToday: number;
  topVarietyToday: string | null;
  topVarietyQuantityToday: number;
}

export interface AvailableStockRow {
  entryId: string;
  cropType: string;
  variety: string | null;
  trayType: string | null;
  availableQuantity: number;
  stockUnit: string;
}

export interface PosStockPage {
  items: AvailableStockRow[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export interface PosDashboard {
  role: "POS";
  scope: SalesSummaryScope;
  kpis: PosKpis;
  todaysWalkIns: OrderRow[];
}

// ── Agronomist placeholder ──────────────────────────────────────

export interface AgronomistPlaceholder {
  role: "Agronomist";
  message: string;
}

// ── Union ───────────────────────────────────────────────────────

export type SalesSummaryData =
  | BusinessUnitSalesDashboard
  | SalesRepDashboard
  | SalesManagerDashboard
  | GmDashboard
  | PosDashboard
  | AgronomistPlaceholder;

// ── API ─────────────────────────────────────────────────────────

function salesSummaryParams(
  scope: string,
  from?: string,
  to?: string,
  filters: SalesSummaryFilters = {},
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = { scope };
  if (from) params.from = from;
  if (to) params.to = to;
  if (filters.regionId) params.regionId = filters.regionId;
  if (filters.businessUnit) params.businessUnit = filters.businessUnit;
  if (filters.businessUnit) params.includeAccounting = false;
  if (filters.cropId) params.cropId = filters.cropId;
  if (filters.varietyId) params.varietyId = filters.varietyId;
  if (filters.search) params.search = filters.search;
  if (filters.sortBy) params.sortBy = filters.sortBy;
  if (filters.sortDescending != null) params.sortDescending = filters.sortDescending;
  if (filters.pageNumber) params.pageNumber = filters.pageNumber;
  if (filters.pageSize) params.pageSize = filters.pageSize;

  return params;
}

export async function getSalesSummary(
  scope: string, from?: string, to?: string, filters: SalesSummaryFilters = {},
): Promise<SalesSummaryData> {
  const params = salesSummaryParams(scope, from, to, filters);
  const res = await apiClient.get<ApiResponse<SalesSummaryData>>(
    "/sales-summary",
    { params },
  );
  return res.data.data;
}

export async function getPosStock(pageNumber: number, pageSize: number, signal?: AbortSignal): Promise<PosStockPage> {
  const res = await apiClient.get<ApiResponse<PosStockPage>>("/sales-summary/pos/stock", {
    params: { pageNumber, pageSize }, signal,
  });
  return res.data.data;
}

export async function getBusinessUnitSalesSection(
  section: "summary" | "regions" | "items",
  scope: string, from?: string, to?: string, filters: SalesSummaryFilters = {},
): Promise<BusinessUnitSalesDashboard> {
  const params = salesSummaryParams(scope, from, to, filters);
  const res = await apiClient.get<ApiResponse<BusinessUnitSalesDashboard>>(
    `/sales-summary/business-unit/${section}`, { params },
  );
  return res.data.data;
}

export async function getSalesSummaryAccounting(
  scope: string,
  from?: string,
  to?: string,
): Promise<FinancialDashboardAccounting> {
  const params: Record<string, string> = { scope };
  if (from) params.from = from;
  if (to) params.to = to;
  const res = await apiClient.get<ApiResponse<FinancialDashboardAccounting>>(
    "/sales-summary/accounting", { params },
  );
  return res.data.data;
}

export async function exportSalesSummaryPdf(
  scope: string, from?: string, to?: string, filters: SalesSummaryFilters = {},
): Promise<void> {
  const response = await apiClient.get<Blob>("/sales-summary/export/pdf", {
    params: salesSummaryParams(scope, from, to, filters), responseType: "blob",
  }).catch(async (error: unknown) => {
    if (isAxiosError(error) && error.response?.data instanceof Blob) {
      const body = await error.response.data.text();
      try { error.response.data = JSON.parse(body); }
      catch { error.response.data = { message: body || "PDF export failed." }; }
    }
    throw error;
  });
  const url = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = `sales-summary-${filters.businessUnit ?? "all"}-${from ?? scope}-${to ?? ""}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
